import React, { useEffect, useState } from "react";
import {
  ArchiveBoxXMarkIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  NoSymbolIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import api from "../services/api";

const apiBaseUrl = process.env.REACT_APP_URL_LOCAL;

const stateMeta = {
  1: { label: "Actif", title: "Professionnels actifs", icon: CheckCircleIcon, accent: "text-emerald-300", badge: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" },
  2: { label: "Supprimé", title: "Professionnels supprimés", icon: ArchiveBoxXMarkIcon, accent: "text-rose-300", badge: "border-rose-500/30 bg-rose-500/10 text-rose-200" },
  3: { label: "Bloqué", title: "Professionnels bloqués", icon: NoSymbolIcon, accent: "text-amber-300", badge: "border-amber-500/30 bg-amber-500/10 text-amber-200" },
};

const UserStatus = ({ user, onChangeState }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${stateMeta[user.EtatID]?.badge || "border-white/10 text-slate-300"}`}>
      {stateMeta[user.EtatID]?.label || "N/A"}
    </span>
    {user.EtatID !== 2 && (
      <button
        type="button"
        onClick={() => onChangeState(user.UtilisateurID, user.EtatID)}
        className={`rounded-md px-3 py-1.5 text-xs font-semibold text-white ${
          user.EtatID === 1 ? "bg-amber-600 hover:bg-amber-500" : "bg-emerald-600 hover:bg-emerald-500"
        }`}
      >
        {user.EtatID === 1 ? "Bloquer" : "Activer"}
      </button>
    )}
  </div>
);

const ProfessionalAccountList = ({ etatId, onStateChange }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const meta = stateMeta[etatId];
  const HeaderIcon = meta?.icon || BuildingOffice2Icon;

  useEffect(() => {
    const loadAccounts = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api.get("/admin/professional-accounts", { params: { etatId } });
        setAccounts(response.data || []);
      } catch (err) {
        setError(err.response?.data?.error || "Impossible de charger les comptes professionnels.");
      } finally {
        setLoading(false);
      }
    };
    loadAccounts();
  }, [etatId]);

  const handleChangeState = async (userId, currentState) => {
    setError("");
    try {
      await api.put("/users/change-etat", {
        userId,
        newEtat: currentState === 1 ? 3 : 1,
      });
      if (onStateChange) onStateChange();
    } catch (err) {
      setError(err.response?.data?.error || "Impossible de modifier l'état de ce compte.");
    }
  };

  const handleChangeAccountState = async (compteId, currentState) => {
    setError("");
    try {
      await api.put(`/admin/professional-accounts/${compteId}/state`, {
        newEtat: currentState === 1 ? 3 : 1,
      });
      if (onStateChange) onStateChange();
    } catch (err) {
      setError(err.response?.data?.error || "Impossible de modifier l'état de cet espace professionnel.");
    }
  };

  return (
    <section className="overflow-hidden rounded-lg border border-sky-500/20 bg-slate-950/60">
      <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-full bg-slate-900 ${meta?.accent || "text-sky-300"}`}>
          <HeaderIcon className="h-5 w-5" />
        </span>
        <div>
          <h3 className="text-base font-semibold text-slate-50">{meta?.title || "Professionnels"}</h3>
          <p className="mt-1 text-xs text-slate-400">{accounts.length} espace{accounts.length > 1 ? "s" : ""}</p>
        </div>
      </div>

      <div className="space-y-4 p-5">
        {loading && <p className="text-sm text-amber-200">Chargement...</p>}
        {error && <p className="rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</p>}
        {!loading && !error && accounts.length === 0 && (
          <p className="rounded-md border border-white/10 bg-slate-900/50 px-4 py-3 text-sm text-slate-400">Aucun compte dans cet état.</p>
        )}
        {accounts.map((account) => account.Principal && (
          <article key={account.CompteID} className="rounded-lg border border-white/10 bg-slate-900/50 p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border border-sky-500/20 bg-slate-950">
                  <img
                    src={account.Principal.CheminImage ? `${apiBaseUrl}${account.Principal.CheminImage}` : "/imageDefault.png"}
                    alt={account.Principal.Surnom}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <p className="truncate font-semibold text-slate-100">{account.RaisonSociale || account.Principal.Surnom}</p>
                  <p className="truncate text-sm text-slate-400">{account.Principal.Surnom} · {account.Principal.Email}</p>
                  <p className="mt-1 text-xs text-slate-500">SIRET {account.Siret || "non renseigné"} · {account.VehiculeCount} véhicule{account.VehiculeCount > 1 ? "s" : ""}</p>
                </div>
              </div>
              <UserStatus
                user={account.Principal}
                onChangeState={(_, currentState) => handleChangeAccountState(account.CompteID, currentState)}
              />
            </div>

            <div className="mt-4 border-t border-white/10 pt-4">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase text-sky-300">
                <UserGroupIcon className="h-4 w-4" />
                {account.Membres.length} compte{account.Membres.length > 1 ? "s" : ""} supplémentaire{account.Membres.length > 1 ? "s" : ""}
              </div>
              {account.Membres.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun membre rattaché.</p>
              ) : (
                <ul className="space-y-2">
                  {account.Membres.map((member) => (
                    <li key={member.UtilisateurID} className="flex flex-col gap-3 rounded-md border border-white/10 bg-slate-950/60 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-200">{member.Surnom}</p>
                        <p className="truncate text-xs text-slate-500">{member.Email}</p>
                      </div>
                      <UserStatus user={member} onChangeState={handleChangeState} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default ProfessionalAccountList;
